/*
 * Purpose: Mission workspace sub-nav — header · nav · flat stepped tool rows
 * Last modified: 2026-07-10 | Agent
 * Completeness: 100
 */
import { Link, useLocation } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { MISSION_SECTIONS, missionNavForSection, matchMissionSection } from '@/lib/missionNav';
import { MissionFlowControls } from '@/components/mission/MissionFlowControls';
import { MissionMapToolNav } from '@/components/mission/MissionMapToolNav';
import { WorkflowSelector } from '@/components/WorkflowSelector';
import { Button } from '@/components/ui/button';
import { useMissionWorkspace } from '@/contexts/MissionWorkspaceContext';
import { PRODUCT_TERMS } from '@/lib/productTerms';
import { cn } from '@/lib/utils';

const SECTION_HREF: Record<(typeof MISSION_SECTIONS)[number]['id'], string> = {
  map: '/mission/map',
  operate: '/mission/operate/scheduler',
  insights: '/mission/insights/logs',
  settings: '/mission/settings/flow',
};

/** Map tools row — sticks under section nav when map chrome scrolls */
export function MissionMapToolsStrip({
  stickyUnderNav = false,
  className,
}: {
  stickyUnderNav?: boolean;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'ab-mission-map-tools',
        stickyUnderNav && 'ab-mission-map-tools--pinned-under-nav',
        className
      )}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2.5">
        <p className="ab-mission-row-label text-[10px] font-medium uppercase tracking-wide mb-1.5 px-0.5">
          Map tools
        </p>
        <MissionMapToolNav />
      </div>
    </div>
  );
}

export function MissionSubNav({ mapStickyChrome = false }: { mapStickyChrome?: boolean }) {
  const { pathname } = useLocation();
  const section = matchMissionSection(pathname);
  const items = missionNavForSection(section);
  const onMap = section === 'map';
  const { onNewFlow } = useMissionWorkspace();

  return (
    <div className="ab-mission-chrome">
      {/* Layer 1 — inverted mission header */}
      <div className="ab-mission-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-3.5">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="min-w-0 flex-1">
              <p className="ab-mission-kicker text-[10px] font-semibold uppercase tracking-wider">
                Mission workspace
              </p>
              <h1 className="ab-mission-title text-lg sm:text-xl font-bold tracking-tight mt-0.5">
                {PRODUCT_TERMS.mission}
              </h1>
              <p className="ab-mission-subtle text-xs mt-1 max-w-xl hidden sm:block">
                {onMap
                  ? 'Map, run, and tune the active flow — actions below replace floating toolbars.'
                  : 'Operate, observe, and configure the selected flow.'}
              </p>
            </div>
            {onMap && (
              <div className="flex flex-wrap items-center gap-2 min-w-0 sm:max-w-md">
                <WorkflowSelector inverted />
              </div>
            )}
            {onNewFlow && (
              <Button
                size="sm"
                variant="outline"
                className="ab-mission-outline-btn shrink-0 h-9"
                onClick={onNewFlow}
              >
                <Plus className="h-4 w-4 mr-1.5" />
                New flow
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Layer 2 — section navigation (Map · Operate · Insights · Settings) */}
      <div className={cn('ab-mission-nav', mapStickyChrome && onMap && 'ab-mission-nav--map-sticky')}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2.5">
          <nav
            className="ab-mission-sections flex flex-wrap gap-1 p-1 rounded-lg border"
            aria-label="Mission sections"
          >
            {MISSION_SECTIONS.map((s) => {
              const isActive = section === s.id;
              return (
                <Link
                  key={s.id}
                  to={SECTION_HREF[s.id]}
                  className={cn(
                    'ab-mission-section-tab px-3 sm:px-4 py-2 rounded-md text-sm font-semibold transition-colors',
                    isActive && 'ab-mission-section-tab--active'
                  )}
                >
                  {s.label}
                </Link>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Layer 3 — flat stepped blocks (no gradient): flow actions → map tools */}
      {onMap && (
        <>
          <div className="ab-mission-flow-actions">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2.5">
              <p className="ab-mission-row-label text-[10px] font-medium uppercase tracking-wide mb-1.5 px-0.5">
                Flow actions
              </p>
              <MissionFlowControls />
            </div>
          </div>
          <MissionMapToolsStrip stickyUnderNav={mapStickyChrome} />
        </>
      )}

      {!onMap && items.length > 0 && (
        <div className="ab-mission-map-tools">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2.5">
            <nav className="flex flex-wrap gap-1.5" aria-label={`${section} pages`}>
              {items.map((item) => {
                const isActive = pathname === item.href || pathname.startsWith(`${item.href}/`);
                return (
                  <Link
                    key={item.id}
                    to={item.href}
                    className={cn(
                      'ab-mission-sub-tab px-3 py-1.5 rounded-md text-sm font-medium border transition-colors whitespace-nowrap',
                      isActive && 'ab-mission-sub-tab--active'
                    )}
                  >
                    {item.label}
                  </Link>
                );
              })}
            </nav>
          </div>
        </div>
      )}
    </div>
  );
}

export default MissionSubNav;
